import { LuSparkles } from 'react-icons/lu';

import { Switch } from '@/components/ui/switch';
import { formatCurrencyShort } from 'utils/number-formatter';

import { MANAGEMENT_FLAGS, TAXABLE_FOOTNOTE } from '../management-flags';
import { OnboardingStepProps } from '../types';

export const FinalReviewStep = ({ accounts, state }: OnboardingStepProps) => {
    const totalAUM = accounts.reduce((sum, a) => sum + Number(a.aum ?? 0), 0);

    return (
        <div className="flex max-w-3xl flex-col gap-8">
            <h3 className="text-cocoa-1000 font-heading text-3xl font-medium">Summary</h3>

            <ReviewSection title="Management">
                <div className="border-cocoa-200 rounded-lg border">
                    <div className="from-gold-50 to-gold-50/40 border-cocoa-200 flex items-center gap-4 border-b bg-gradient-to-r px-4 py-3">
                        <div className="from-gold-300 via-gold-400 to-gold-600 flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-gradient-to-br text-white">
                            <LuSparkles className="h-5 w-5" />
                        </div>
                        <div className="flex-1 text-sm">
                            <span className="text-cocoa-1000 font-semibold">Portfolio Boost</span>
                            <span className="text-cocoa-600">
                                {' '}
                                · {state.portfolioBoostEnabled ? 'Enabled' : 'Disabled'}
                            </span>
                        </div>
                    </div>
                    <ul className="divide-cocoa-200 divide-y">
                        {MANAGEMENT_FLAGS.map((flag) => {
                            const Icon = flag.icon;
                            const isToggle = !!flag.toggleKey;
                            const enabled = isToggle ? state.management[flag.toggleKey!] : true;
                            return (
                                <li key={flag.title} className="flex items-start gap-4 px-4 py-3">
                                    <div className="bg-cocoa-50 text-cocoa-700 flex h-10 w-10 shrink-0 items-center justify-center rounded-md">
                                        <Icon className="h-5 w-5" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="text-cocoa-1000 text-sm font-medium">
                                            {flag.title}
                                            {flag.requiresTaxable && <span className="text-cocoa-500"> *</span>}
                                        </div>
                                        <p className="text-cocoa-600 mt-0.5 text-sm">{flag.description}</p>
                                    </div>
                                    {isToggle && (
                                        <Switch checked={enabled} disabled aria-label={`${flag.title} status`} />
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </div>
                <p className="text-cocoa-500 mt-2 text-right text-xs italic">{TAXABLE_FOOTNOTE}</p>
            </ReviewSection>

            <ReviewSection title="Accounts">
                <div className="flex items-center justify-between rounded-lg border px-5 py-4 text-sm">
                    <span className="text-cocoa-1000 font-medium">
                        {accounts.length} {accounts.length === 1 ? 'account' : 'accounts'}
                    </span>
                    <span className="text-cocoa-700">
                        Total AUM:{' '}
                        <span className="text-cocoa-1000 font-semibold">{formatCurrencyShort(totalAUM)}</span>
                    </span>
                </div>
            </ReviewSection>

            <div className="text-cocoa-600 mt-2 rounded-md px-2 text-xs leading-relaxed">
                By submitting, you confirm that these accounts will be managed by the portfolio engine according to the
                settings above. Trading status will switch from Manual to Automatic and trades may be placed on the next
                eligible window.
            </div>
        </div>
    );
};

const ReviewSection = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <section>
        <h4 className="text-cocoa-1000 mb-2 text-base font-medium">{title}</h4>
        {children}
    </section>
);
